/**
 * KB 연동 스텝 추적 훅
 * 
 * KB 연동 프로세스의 각 단계를 추적
 * - kb_connect_step_view: 스텝 진입
 * - kb_connect_step_submit: 스텝 완료 (성공/실패)
 * 
 * ⚠️ intent_id는 내부적으로 kb_connect_flow 의도의 intent_id를 세션 스토리지에서 자동으로 참조합니다.
 * 
 * @example
 * ```tsx
 * function KBConnectStepPage() {
 *   const kbConnectStep = useKBConnectStep();
 *   const [currentStep, setCurrentStep] = useState<KBConnectStepKey>('search_select');
 *   
 *   
 *   const handleSubmitStep = async (data: any) => {
 *     try {
 *       await processStep(currentStep, data);
 *       
 *       // 스텝 완료 (성공)
 *       kbConnectStep.submit({
 *         step_key: currentStep,
 *         step_index: 1,
 *         outcome: 'ok',
 *         tickers: data.tickers,  // search_select 시
 *       });
 *       
 *       setCurrentStep(getNextStep(currentStep));
 *     } catch (error) {
 *       // 스텝 완료 (실패)
 *       kbConnectStep.submit({
 *         step_key: currentStep,
 *         step_index: 1,
 *         outcome: 'fail',
 *         fail_reason: 'validation_error',
 *       });
 *     }
 *   };
 * }
 * ```
 */

import { useCallback } from 'react';
import { ulid } from 'ulid';
import { track } from '../../../../core/client';
import { getIntentId } from '../../../../core/ids';
import type { KBConnectStepKey } from '../../../../core/schema';


export interface KBConnectStepViewParams {
    step_key: KBConnectStepKey;
}

export interface KBConnectStepSubmitParams {
    step_key: KBConnectStepKey;
    sub_step_index?: number;
    outcome: 'ok' | 'fail';
    fail_reason?: string;
    choice?: 'input' | 'skip' | 'proceed';
    submit_mode?: 'client_only' | 'server_request';
    tickers?: string[];
    weights?: number[];
    answer_key?: string;
}

export interface KBConnectStepActions {
    view: (params: KBConnectStepViewParams) => void;
    submit: (params: KBConnectStepSubmitParams) => void;
}

/**
 * 포트폴리오 스텝 추적 훅
 */
export function useKBConnectStep(): KBConnectStepActions {

    const getKBConnectFlowIntentId = useCallback((): string | null => {
        const kbConnectFlowIntent = getIntentId('kb_connect_flow');
        if (kbConnectFlowIntent?.intentId) {
            return kbConnectFlowIntent.intentId;
        }
        // fallback: portfolio_flow가 없으면 새로 생성 (정상적인 flow에서는 발생하지 않아야 함)
        console.warn('[KBConnectStep] kb_connect_flow intent_id not found in session storage, generating new one');
        return null; //ulid(); // 회원 - 설문 재시도 시 portfolio_flow 의도가 없어서 null 반환
    }, []);

    /**
     * 스텝 진입 추적
     */
    const view = useCallback((params: KBConnectStepViewParams) => {
        const intentId = getKBConnectFlowIntentId();

        track('kb_connect_step_view', {
            intent_id: intentId,
            step_key: params?.step_key,
        });

        console.log(`👀 [KBConnectStep] View: ${params.step_key} - [intent_id: ${intentId}]`);
    }, [getKBConnectFlowIntentId]);

    /**
     * 스텝 완료 추적
     */
    const submit = useCallback((params: KBConnectStepSubmitParams) => {
        const intentId = getKBConnectFlowIntentId();

        track('kb_connect_step_submit', {
            intent_id: intentId,
            step_key: params?.step_key,
            sub_step_index: params?.sub_step_index,
            outcome: params?.outcome,
            fail_reason: params?.fail_reason,
        });

        // const emoji = params.outcome === 'ok' ? '✅' : '❌';
        // console.log(`${emoji} [PortfolioStep] Submit: ${params.step_key}- ${params.outcome} [intent_id: ${intentId}]`);
    }, [getKBConnectFlowIntentId]);

    return {
        view,
        submit,
    };
}

export type { KBConnectStepKey };

